
import { defineComponent, PropType } from 'vue'

import ExportRunAsMarkdownGQL from '~/apollo/queries/repository/runs/run/exportAsMarkdown.gql'
import { getPackageManagerIcon } from '~/utils/dependencies'
import { getBackRoute } from '~/utils/router'
import { getRunProps } from '~/utils/runs'
import { gateStatusIconSrc } from '~/utils/ui'

import type { ApolloQueryResult } from 'apollo-client'

import {
  type Check,
  type ExportRunAsMarkdownQuery,
  type ExportRunAsMarkdownQueryVariables,
  type Run,
  RunReportStatus,
  RunStatus,
  type ScaCheck
} from '~/types/types'

export default defineComponent({
  name: 'CheckLayoutWrapper',
  props: {
    run: {
      type: Object as PropType<Run>,
      required: true
    },
    checks: {
      type: Array as PropType<Check[]>,
      required: true
    },
    scaChecks: {
      type: Array as PropType<ScaCheck[]>,
      default: () => []
    },
    canRetryAnalysis: {
      type: Boolean,
      default: false
    },
    retryableChecks: {
      type: Array as PropType<Check[]>,
      default: () => []
    },
    analyzersInRetry: {
      type: Set as PropType<Set<string>>,
      default: () => new Set()
    },
    flashActiveAnalyzer: {
      type: Boolean,
      default: false
    },
    showArchivedRunEmptyState: {
      type: Boolean,
      default: false
    },
    showSkipEmptyState: {
      type: Boolean,
      default: false
    },
    provider: {
      type: String,
      required: true
    }
  },
  emits: ['retry-checks'],
  data() {
    return {
      hasCopiedMarkdown: false,
      isCopyingMarkdown: false
    }
  },
  computed: {
    iconSrc(): string {
      return gateStatusIconSrc(this.run.status)
    },
    isRunInProgress(): boolean {
      return this.run.status === RunStatus.Pend
    },
    backRoute() {
      const fallbackName = this.run.isForDefaultBranch
        ? 'provider-owner-repo-history-baseline'
        : 'provider-owner-repo-history-pull-requests'
      return getBackRoute(this.$route, { fallbackName })
    },
    runProps() {
      return getRunProps(this.run)
    },
    pillIcon(): string {
      return this.$providerMetaMap[this.provider]?.icon ?? ''
    },
    retryableAnalyzers(): string[] {
      return this.retryableChecks
        .map((check) => check.analyzer?.shortcode)
        .filter(Boolean) as string[]
    },
    areAllChecksRetrying(): boolean {
      return this.retryableChecks.every((check) =>
        this.analyzersInRetry.has(check.analyzer?.shortcode || '')
      )
    },
    isFreshLoad(): boolean {
      return this.$route.params.runId !== this.run.runId
    },
    hasReportCard(): boolean {
      return Boolean(this.run.reportCard) && !this.run.isForDefaultBranch
    },
    isReportCardReady(): boolean {
      return this.run.reportCard?.status === RunReportStatus.Completed
    },
    isReportCardSkipped(): boolean {
      return this.run.reportCard?.status === RunReportStatus.Skipped
    },
    isReportCardActive(): boolean {
      return this.$route.name === 'provider-owner-repo-run-runId' && this.hasReportCard
    },
    reportCardRoute(): string {
      const { provider, owner, repo, runId } = this.$route.params
      return `/${provider}/${owner}/${repo}/run/${runId}`
    },
    copyMarkdownIcon(): string {
      if (this.isCopyingMarkdown) return 'spin-loader'
      return this.hasCopiedMarkdown ? 'check' : 'square-m'
    },
    copyMarkdownLabel(): string {
      if (this.isCopyingMarkdown) return 'Copying...'
      return this.hasCopiedMarkdown ? 'Copied!' : 'Copy as Markdown'
    }
  },
  methods: {
    getPackageManagerIcon,
    scaTargetLabel(scaCheck: ScaCheck): string {
      const manifest = scaCheck.target?.manifestPath || ''
      const lockfile = scaCheck.target?.lockfilePath || ''
      if (manifest && lockfile) return `${manifest}/${lockfile}`
      if (manifest || lockfile) return manifest || lockfile
      return 'SCA target'
    },
    async copyAsMarkdown(analyzerShortcode = '', targetId = ''): Promise<void> {
      if (this.isCopyingMarkdown) return
      this.isCopyingMarkdown = true
      try {
        const args: ExportRunAsMarkdownQueryVariables = {
          runId: this.run.runId
        }

        if (analyzerShortcode) {
          args['analyzerShortcode'] = analyzerShortcode
        } else if (targetId) {
          args['targetId'] = targetId
        }

        const response: ApolloQueryResult<ExportRunAsMarkdownQuery> = await this.$fetchGraphqlData(
          ExportRunAsMarkdownGQL,
          args
        )
        const markdown = response.data.exportRunAsMarkdown
        if (markdown) {
          await this.$copyToClipboard(markdown)
          this.hasCopiedMarkdown = true
          setTimeout(() => {
            this.hasCopiedMarkdown = false
          }, 1000)
        }
      } catch {
        this.$toast.danger('Failed to copy run as markdown.')
      } finally {
        this.isCopyingMarkdown = false
      }
    }
  }
})
